/**
 * Stack & Clear spend-tier offer bar for the cart, plus static tier amounts (collection banner).
 * Converts AUD thresholds to the active market currency (Shopify.currency.rate),
 * unless the element carries market-specific local amounts (data-local).
 */
const scRate = () => {
  const rate = parseFloat(window.Shopify && window.Shopify.currency ? window.Shopify.currency.rate : '1');
  return Number.isFinite(rate) && rate > 0 ? rate : 1;
};

const scFormat = (cents, currency, locale, decimals = 0) => {
  try {
    return new Intl.NumberFormat(locale || document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency || 'AUD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(cents / 100);
  } catch (error) {
    return `${(cents / 100).toFixed(decimals)} ${currency}`;
  }
};

class ScTierBar extends HTMLElement {
  static get observedAttributes() {
    return ['data-subtotal', 'data-currency'];
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.render();
  }

  animateFill(fill, progress) {
    /* The cart is re-rendered (morphed) on every change, which would reset the bar to 0 and
       make it jump. Remember the last position and animate from there to the new one. */
    let prev = null;
    try {
      const stored = parseFloat(window.sessionStorage.getItem('sc_tier_progress'));
      if (Number.isFinite(stored)) prev = stored;
    } catch (error) {
      prev = null;
    }
    const target = `${progress.toFixed(2)}%`;
    if (prev === null || Math.abs(prev - progress) < 0.01) {
      fill.style.transition = 'none';
      fill.style.width = target;
    } else {
      fill.style.transition = 'none';
      fill.style.width = `${prev.toFixed(2)}%`;
      void fill.offsetWidth;
      fill.style.transition = 'width 0.7s ease';
      requestAnimationFrame(() => {
        fill.style.width = target;
      });
    }
    try {
      window.sessionStorage.setItem('sc_tier_progress', String(progress));
    } catch (error) {
      /* storage unavailable */
    }
  }

  format(cents) {
    return scFormat(cents, this.dataset.currency, this.dataset.locale);
  }

  render() {
    const subtotal = parseInt(this.dataset.subtotal || '0', 10) || 0;
    const base = (this.dataset.thresholds || '').split(',').map(Number).filter((n) => n > 0);
    const percents = (this.dataset.percents || '').split(',').map(Number);
    if (!base.length) return;

    const noConvert = this.dataset.currency === this.dataset.shopCurrency || this.dataset.local === 'true';
    const rate = noConvert ? 1 : scRate();
    const thresholds = base.map((t) => (noConvert ? t : Math.ceil((t * rate) / 100) * 100));
    const count = thresholds.length;

    let reached = -1;
    thresholds.forEach((t, i) => {
      if (subtotal >= t) reached = i;
    });

    this.querySelectorAll('[data-tier-amount]').forEach((el, i) => {
      if (thresholds[i] != null) el.textContent = this.format(thresholds[i]);
    });

    this.querySelectorAll('[data-tier]').forEach((el, i) => {
      el.classList.toggle('is-active', i === reached);
      el.classList.toggle('is-reached', i <= reached);
    });

    const center = (i) => ((2 * i + 1) / (2 * count)) * 100;
    let progress = 100;
    if (reached === -1) {
      progress = Math.min(1, subtotal / thresholds[0]) * center(0);
    } else if (reached < count - 1) {
      const from = thresholds[reached];
      const to = thresholds[reached + 1];
      const ratio = Math.min(1, Math.max(0, (subtotal - from) / (to - from)));
      progress = center(reached) + ratio * (center(reached + 1) - center(reached));
    }
    const fill = this.querySelector('[data-tier-fill]');
    if (fill) this.animateFill(fill, progress);

    const msg = this.querySelector('[data-tier-msg]');
    if (msg) {
      if (reached >= count - 1) {
        msg.textContent = this.dataset.msgMax || '';
      } else {
        const next = thresholds[reached + 1];
        const pct = percents[reached + 1] || 0;
        const remaining = Math.max(0, next - subtotal);
        const remainingRounded = Math.ceil(remaining / 100) * 100;
        const minSaving = Math.floor((next * pct) / 100 / 100) * 100;
        msg.textContent = (this.dataset.msgNext || '')
          .replace('[amount]', this.format(remainingRounded))
          .replace('[pct]', String(pct))
          .replace('[save]', this.format(minSaving));
      }
    }

    this.setAttribute('data-ready', '');
  }
}

if (!customElements.get('sc-tier-bar')) {
  customElements.define('sc-tier-bar', ScTierBar);
}

/* Static tier amounts outside the cart (e.g. collection banner) */
const scConvertAmounts = () => {
  document.querySelectorAll('[data-sc-amount]:not([data-sc-done])').forEach((el) => {
    const base = parseInt(el.dataset.scAmount, 10) || 0;
    const currency = el.dataset.currency;
    const same = currency === el.dataset.shopCurrency;
    const cents = same ? base : Math.ceil((base * scRate()) / 100) * 100;
    el.textContent = scFormat(cents, currency, el.dataset.locale);
    el.setAttribute('data-sc-done', '');
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scConvertAmounts);
} else {
  scConvertAmounts();
}
